/**
 * Agent memory tools — search, list, and delete memories.
 *
 * Uses a factory function so that workbookKey is captured via closure,
 * keeping the tool API clean for the agent.
 */

import { tool } from 'ai'
import {
  deleteMemory as deleteMemoryApi,
  listMemories as listMemoriesApi,
  searchMemories as searchMemoriesApi,
} from '@/lib/api-handlers/memories'
import { toolSchemas } from './tool-schemas'

export function createMemoryTools(workbookKey: string | null) {
  const searchMemories = tool({
    ...toolSchemas.searchMemories,
    execute: async ({ query, limit }) => {
      try {
        const results = await searchMemoriesApi(
          query,
          'both',
          workbookKey ?? undefined,
          limit
        )
        return {
          success: true,
          count: results.length,
          memories: results.map((m) => ({
            id: m.id,
            content: m.content,
            type: m.type,
            scope: m.scope,
          })),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  })

  const listMemoriesToolFn = tool({
    ...toolSchemas.listMemories,
    execute: async ({ scope }) => {
      try {
        const results = await listMemoriesApi(
          scope ?? 'both',
          workbookKey ?? undefined
        )
        return {
          success: true,
          count: results.length,
          memories: results.map((m) => ({
            id: m.id,
            content: m.content,
            type: m.type,
            scope: m.scope,
          })),
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  })

  const deleteMemory = tool({
    ...toolSchemas.deleteMemory,
    execute: async ({ memoryId }) => {
      try {
        await deleteMemoryApi(memoryId)
        return { success: true }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  })

  return {
    searchMemories,
    listMemories: listMemoriesToolFn,
    deleteMemory,
  }
}
